import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useHistory } from 'react-router-dom'
import { DSText, DSSpacer } from '@zoe/ds-web'
import { onNextClickType } from '../types'
import { SCREEN_MAX_WIDTH } from '../core/constants'

// "Why ZOE is different" (ZOE-promise) — an auto-playing carousel shown near the
// end of the e7 funnel (Figma sections 9a/9b). Seven steps per branch, each with
// its own illustration and headline. Consecutive steps share a background colour
// and status line (grouped in pairs: cream, navy, teal, then aubergine for the
// last), so the background cross-fades and the card slides only when the colour
// group changes. Two branches: weight-loss and non-weight-loss (via `weightLoss`).

const IMG = '/img/e7/zoe-promise'

type PromiseStep = {
  background: string
  status: string
  headline: string
  image: string
  imageAlt: string
}

const STEPS_NO_WEIGHT_LOSS: PromiseStep[] = [
  {
    background: 'var(--colour-surface-cream-subtle)',
    status: 'Building your plan',
    headline: 'ZOE transforms your health with the power of your gut',
    image: `${IMG}/01-food-phone.jpg`,
    imageAlt: 'A phone scanning a plate of food to identify its ingredients',
  },
  {
    background: 'var(--colour-surface-cream-subtle)',
    status: 'Building your plan',
    headline: 'We’ll show you how to eat for gut health in a few minutes each day',
    image: `${IMG}/02-plants.jpg`,
    imageAlt: 'A person on their phone tracking 28 of 30 unique plants this week',
  },
  {
    background: 'var(--colour-surface-navy-subtle)',
    status: 'Saving your preferences',
    headline: 'Eating for gut health will help you feel better and reach your goals sustainably',
    image: `${IMG}/03-body.jpg`,
    imageAlt: 'A person with tags for better digestion, more energy and improved mood',
  },
  {
    background: 'var(--colour-surface-navy-subtle)',
    status: 'Saving your preferences',
    headline: 'We focus on abundance not restriction. You’ll eat more of the right foods, not less.',
    image: `${IMG}/04-berries.jpg`,
    imageAlt: 'A bowl of berries and nuts scoring 72, next to croissants scoring 29',
  },
  {
    background: 'var(--colour-surface-teal-subtle)',
    status: 'Personalising your advice',
    headline: 'Get personalised food advice based on the world’s largest gut health study',
    image: `${IMG}/05-scientist.jpg`,
    imageAlt: 'A ZOE scientist holding a clipboard surrounded by fresh vegetables',
  },
  {
    background: 'var(--colour-surface-teal-subtle)',
    status: 'Personalising your advice',
    headline: 'Understand how food affects your body and take control of your health',
    image: `${IMG}/06-score.jpg`,
    imageAlt: 'A balanced meal bowl scoring 80, tagged as boosting gut health and keeping you full',
  },
  {
    background: 'var(--colour-surface-aubergine-subtle)',
    status: 'Finishing up',
    headline: 'We’ll help you build simple, lasting habits that fit into your life',
    image: `${IMG}/07-diet-score.jpg`,
    imageAlt: 'A phone showing a diet quality score of 60 between two meal bowls',
  },
]

const STEPS_WEIGHT_LOSS: PromiseStep[] = [
  STEPS_NO_WEIGHT_LOSS[0],
  STEPS_NO_WEIGHT_LOSS[1],
  {
    background: 'var(--colour-surface-navy-subtle)',
    status: 'Saving your preferences',
    headline: 'Eating for gut health will help you feel better and lose weight without dieting or restriction',
    image: `${IMG}/03-body-wl.jpg`,
    imageAlt: 'A person with tags for more energy, weight loss and improved mood',
  },
  {
    background: 'var(--colour-surface-navy-subtle)',
    status: 'Saving your preferences',
    headline: 'The best part? We focus on abundance. You’ll eat more of the right foods, not less.',
    image: `${IMG}/04-berries.jpg`,
    imageAlt: 'A bowl of berries and nuts scoring 72, next to croissants scoring 29',
  },
  {
    background: 'var(--colour-surface-teal-subtle)',
    status: 'Personalising your advice',
    headline: 'Get personalised food advice based on the world’s largest nutrition study',
    image: `${IMG}/05-scientist.jpg`,
    imageAlt: 'A ZOE scientist holding a clipboard surrounded by fresh vegetables',
  },
  {
    background: 'var(--colour-surface-teal-subtle)',
    status: 'Personalising your advice',
    headline: 'Build a healthier, more mindful and guilt-free relationship with food',
    image: `${IMG}/06-score.jpg`,
    imageAlt: 'A balanced meal bowl scoring 80, tagged as boosting gut health and keeping you full',
  },
  {
    background: 'var(--colour-surface-aubergine-subtle)',
    status: 'Finishing up',
    headline: 'We’ll help you make simple, lasting changes that fit into your life',
    image: `${IMG}/07-diet-score.jpg`,
    imageAlt: 'A phone showing a diet quality score of 60 between two meal bowls',
  },
]

// Set when the carousel auto-advances forward, so navigating back to it from the
// next screen steps further back rather than replaying + trapping the user in a
// forward loop (mirrors SymptomScoreCalculatingScreen).
export const VISITED_ZOE_PROMISE_KEY = 'visited_zoe_promise'

// sessionStorage access can throw (sandboxed iframes / disabled storage) or be
// undefined (non-browser); guard so it never crashes the screen.
const safeSession = {
  get: (key: string): string | null => {
    try {
      return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null
    } catch {
      return null
    }
  },
  set: (key: string, value: string): void => {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value)
    } catch {
      /* storage unavailable — ignore */
    }
  },
  remove: (key: string): void => {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(key)
    } catch {
      /* storage unavailable — ignore */
    }
  },
}

// Animation timings for the auto-play sequence (Figma 9a/9b spec): the screen (or,
// within a colour group, just the image) slides in first with the bottom status
// text; then the top text reveals one line at a time — starting with "Why ZOE is
// different" and ending on the last headline line — before a pause and auto-advance.
const ENTER_MS = 500 // slide-in duration before the top text starts revealing
const LINE_STAGGER_MS = 250 // one line of top text reveals every 250ms
const LINE_ANIM_MS = 700 // each line's fade/slide-in duration
const PAUSE_MS = 3000 // hold after the last line before auto-advancing

// Each line of the top text fades in from just above ("transitioning in from the top").
const lineIn = keyframes`
  from { opacity: 0; transform: translateY(-14px); }
  to { opacity: 1; transform: translateY(0); }
`

// A screen — or, within a colour group, just the image — slides in from the right.
const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`

const Background = styled.div<{ $color: string }>`
  position: fixed;
  inset: 0;
  z-index: -1;
  background-color: ${({ $color }) => $color};
  transition: background-color 0.5s ease;
`

const Layout = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Fill the area below the funnel nav so the status line can sit near the
     bottom of the screen. */
  min-height: calc(100vh - 130px);
  /* Centred column capped to the funnel width, with 20px side padding so the
     headline + image fill the space on any viewport (no wide side margins on
     larger phones). Bottom padding 0 keeps the status text near the page bottom. */
  width: 100%;
  max-width: ${SCREEN_MAX_WIDTH}px;
  margin: 0 auto;
  padding: 24px 20px 0;
  text-align: center;
`

// The whole screen (image + status, with the top text still hidden) slides in when
// the colour group changes. Keyed on the background so it only replays then; within
// a colour group it stays put and only the image re-animates.
const ScreenSlide = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${slideInRight} ${ENTER_MS}ms ease both;
`

const TopTextWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

// The "Why ZOE is different" label — the first line of the top-text reveal.
const Label = styled.div`
  opacity: 0;
  animation: ${lineIn} ${LINE_ANIM_MS}ms ease both;
  animation-delay: ${ENTER_MS}ms;
`

const HeadlineWrapper = styled.div`
  width: 100%;
  /* Reserve room for up to three lines (at the ~36px line-height) so the image
     below sits at a consistent position and doesn't jump as the headline changes. */
  min-height: 108px;
`

// Heading "Alt Semibold" at the DS fluid-heading-600 size (~30px across all real
// viewports). The font-size comes from the variant; we only keep a comfortable,
// size-relative line-height + letter-spacing. weight="semiBold" keeps the alt face.
const Headline = styled(DSText)`
  && {
    line-height: 1.2;
    letter-spacing: -0.02em;
  }
`

// The per-word animation-delay is applied inline (not as a styled prop) so that
// styled-components doesn't generate a fresh class per unique delay. Longhand
// animation props (not the shorthand) so the inline animation-delay isn't reset.
const Word = styled.span<{ $animate: boolean }>`
  display: inline-block;
  white-space: pre;
  opacity: 0;
  ${({ $animate }) =>
    $animate &&
    css`
      animation-name: ${lineIn};
      animation-duration: ${LINE_ANIM_MS}ms;
      animation-timing-function: ease;
      animation-fill-mode: both;
    `}
`

const StepImage = styled.img<{ $slide: boolean }>`
  display: block;
  width: 100%;
  aspect-ratio: 335 / 253;
  object-fit: cover;
  border-radius: 16px;
  margin-top: 40px;
  /* Within a colour group the image slides in from the right on its own; on a
     colour-group change it stays static and rides the ScreenSlide instead. */
  ${({ $slide }) =>
    $slide &&
    css`
      animation: ${slideInRight} ${ENTER_MS}ms ease both;
    `}
`

// Flexible space between the image and the status line at the bottom.
const BottomSpacer = styled.div`
  flex: 1;
  min-height: 24px;
`

const StatusRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
`

const Dot = styled.span<{ $index: number }>`
  animation: ${dotPulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ $index }) => `${$index * 0.2}s`};
`

// The top text: the "Why ZOE is different" label followed by the headline, revealed
// one line at a time after the screen/image has slid in. Headline words are measured
// after layout and grouped into visual lines; every word in a line shares a delay so
// the line animates as a unit. The label is the first line (delay ENTER_MS) and each
// headline line follows LINE_STAGGER_MS apart. onLines reports the headline's line
// count so the parent can time the pause + auto-advance.
const TopText: React.FC<{ label: string; headline: string; onLines: (n: number) => void }> = ({
  label,
  headline,
  onLines,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [delays, setDelays] = useState<number[] | null>(null)
  const words = headline.split(' ')
  const onLinesRef = useRef(onLines)
  useEffect(() => {
    onLinesRef.current = onLines
  }, [onLines])

  useLayoutEffect(() => {
    let cancelled = false
    // Group words into visual lines by their vertical position; every word on a line
    // shares a delay so the whole line reveals as a unit.
    const measure = () => {
      if (cancelled) return
      const el = ref.current
      if (!el) return
      const spans = Array.from(el.querySelectorAll<HTMLElement>('[data-word]'))
      let lineIndex = -1
      let lastTop: number | null = null
      const nextDelays = spans.map((span) => {
        const top = span.offsetTop
        if (lastTop === null || Math.abs(top - lastTop) > 2) {
          lineIndex += 1
          lastTop = top
        }
        // The label takes the first line slot, so headline line j (0-based) reveals at
        // ENTER_MS + (j + 1) * LINE_STAGGER_MS.
        return ENTER_MS + (lineIndex + 1) * LINE_STAGGER_MS
      })
      setDelays(nextDelays)
      onLinesRef.current(lineIndex + 1)
    }

    // Measuring with the fallback font wraps the headline differently from the final
    // web font, which makes the reveal break mid-sentence. Wait for the web font
    // before measuring (it resolves synchronously once cached, i.e. from screen 2 on),
    // and re-measure on resize so the line breaks stay clean at any viewport width.
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined
    if (!fonts || fonts.status === 'loaded') measure()
    else fonts.ready.then(measure)
    // Throttle resize to one measure per frame — measure() reads offsetTop (forces
    // reflow) and sets state, so running it on every resize event thrashes layout.
    let rafId = 0
    const onResize = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(measure)
    }
    window.addEventListener('resize', onResize)
    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', onResize)
    }
  }, [headline])

  return (
    <TopTextWrap>
      <Label>
        <DSText variant="fluid-paragraph-200" weight="regular" color="secondary" align="center">
          {label}
        </DSText>
      </Label>
      <DSSpacer size={12} direction="vertical" />
      <HeadlineWrapper ref={ref}>
        {/* aria-label carries the whole headline for screen readers; the per-word
            spans (needed for the line-by-line reveal) are hidden from them so it
            isn't announced word by word with pauses. */}
        <Headline variant="fluid-heading-600" weight="semiBold" align="center" aria-label={headline}>
          <span aria-hidden="true">
            {words.map((word, i) => (
              <Word
                key={i}
                data-word
                $animate={delays !== null}
                style={{ animationDelay: `${delays ? delays[i] : 0}ms` }}
              >
                {i === 0 ? word : ` ${word}`}
              </Word>
            ))}
          </span>
        </Headline>
      </HeadlineWrapper>
    </TopTextWrap>
  )
}

const AnimatedEllipsis: React.FC = () => (
  <>
    <Dot $index={0}>.</Dot>
    <Dot $index={1}>.</Dot>
    <Dot $index={2}>.</Dot>
  </>
)

export type ZoePromiseScreenProps = {
  weightLoss?: boolean
}

type Props = ZoePromiseScreenProps & {
  onNextClick: onNextClickType
}

export const ZoePromiseScreen: React.FC<Props> = ({ weightLoss = false, onNextClick }) => {
  const history = useHistory()
  const steps = weightLoss ? STEPS_WEIGHT_LOSS : STEPS_NO_WEIGHT_LOSS
  const [stepIndex, setStepIndex] = useState(0)
  // Visual line count of the current headline (measured after layout), used to time
  // when the reveal finishes so the pause + auto-advance start at the right moment.
  const [headlineLines, setHeadlineLines] = useState(2)

  // Warm every step's image on mount so each one is decoded and cached before its
  // step animates — otherwise the first watch janks while an image downloads mid
  // slide-in (later watches are smooth because the images are already cached).
  const preloadRef = useRef<HTMLImageElement[]>([])
  useEffect(() => {
    preloadRef.current = steps.map((step) => {
      const img = new Image()
      img.src = step.image
      return img
    })
  }, [steps])

  const onNextClickRef = useRef(onNextClick)
  useEffect(() => {
    onNextClickRef.current = onNextClick
  }, [onNextClick])

  const cameBackRef = useRef(false)
  useEffect(() => {
    if (history.action === 'POP' && safeSession.get(VISITED_ZOE_PROMISE_KEY) === 'true') {
      safeSession.remove(VISITED_ZOE_PROMISE_KEY)
      cameBackRef.current = true
      history.goBack()
    }
  }, [history])

  useEffect(() => {
    if (cameBackRef.current) return
    // The last headline line finishes revealing at ENTER_MS + headlineLines *
    // LINE_STAGGER_MS + LINE_ANIM_MS; then hold for PAUSE_MS before advancing.
    const stepMs = ENTER_MS + headlineLines * LINE_STAGGER_MS + LINE_ANIM_MS + PAUSE_MS
    const timer = setTimeout(() => {
      if (stepIndex + 1 >= steps.length) {
        safeSession.set(VISITED_ZOE_PROMISE_KEY, 'true')
        ;(onNextClickRef.current as () => void)()
      } else {
        setStepIndex((i) => i + 1)
      }
    }, stepMs)
    return () => clearTimeout(timer)
    // `steps` is a stable reference to a module-level constant, so depending on it
    // directly (rather than steps.length) re-runs correctly if the branch changes.
  }, [stepIndex, headlineLines, steps])

  const step = steps[stepIndex]
  // Within a colour group only the image slides in (background + status stay static);
  // on a colour-group change the whole screen slides in instead.
  const isWithinGroup = stepIndex > 0 && steps[stepIndex].background === steps[stepIndex - 1].background

  return (
    <>
      <Background $color={step.background} />
      <Layout>
        {/* Keyed on the background: remounts (and slides in) only on a colour-group
            change; within a group it persists so the status text stays static. */}
        <ScreenSlide key={step.background}>
          {/* Distinct keys per step so each remounts and replays its animation. The
              two prefixes matter: sharing `stepIndex` as a bare key across these
              siblings collides and leaves the previous step's text in the DOM. */}
          <TopText
            key={`text-${stepIndex}`}
            label="Why ZOE is different"
            headline={step.headline}
            onLines={setHeadlineLines}
          />
          <StepImage
            key={`img-${stepIndex}`}
            src={step.image}
            alt={step.imageAlt}
            $slide={isWithinGroup}
            decoding="async"
          />
          <BottomSpacer aria-hidden />
          <StatusRow>
            <DSText variant="fluid-paragraph-200" weight="regular" color="secondary" align="center">
              {step.status}
            </DSText>
            <DSText variant="fluid-paragraph-200" weight="regular" color="secondary">
              <AnimatedEllipsis />
            </DSText>
          </StatusRow>
        </ScreenSlide>
      </Layout>
    </>
  )
}
